
import { supabase } from "@/integrations/supabase/client";

interface SecurityValidation {
  isValid: boolean;
  userId: string | null;
  errors: string[];
}

interface SecurityContext {
  isAuthenticated: boolean;
  userId: string | null;
  sessionValid: boolean;
  profileExists: boolean;
}

export class SecurityService {
  private static instance: SecurityService;
  private cache: Map<string, { context: SecurityContext; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 60000; // 1 minute cache

  static getInstance(): SecurityService {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService();
    }
    return SecurityService.instance;
  }

  async validateSecurityContext(): Promise<SecurityContext> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error || !user) {
        return {
          isAuthenticated: false,
          userId: null,
          sessionValid: false,
          profileExists: false
        };
      }

      // Check cache first
      const cacheKey = user.id;
      const cached = this.cache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
        return cached.context;
      }

      // Simple profile check
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      const context: SecurityContext = {
        isAuthenticated: true,
        userId: user.id,
        sessionValid: true,
        profileExists: !!profile
      };

      // Cache the result
      this.cache.set(cacheKey, {
        context,
        timestamp: Date.now()
      });

      return context;
    } catch (error) {
      console.error('Security validation failed:', error);
      return {
        isAuthenticated: false,
        userId: null,
        sessionValid: false,
        profileExists: false
      };
    }
  }

  async logSecurityEvent(action: string, resource?: string, metadata?: Record<string, any>) {
    try {
      await supabase.rpc('log_user_action', {
        p_action_type: action,
        p_resource: resource || null,
        p_metadata: metadata || {}
      });
    } catch (error) {
      // Silent fail for logging
    }
  }

  async validateUserOperation(operation: string, resourceId?: string): Promise<SecurityValidation> {
    const context = await this.validateSecurityContext();
    const errors: string[] = [];

    if (!context.isAuthenticated) {
      errors.push('User not authenticated');
    }

    if (!context.sessionValid) {
      errors.push('Invalid session');
    }

    if (!context.profileExists) {
      errors.push('User profile not found');
    }

    return {
      isValid: errors.length === 0,
      userId: context.userId,
      errors
    };
  }

  clearCache(userId?: string) {
    if (userId) {
      this.cache.delete(userId);
    } else {
      this.cache.clear();
    }
  }
}

export const securityService = SecurityService.getInstance();
